import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { REACT_APP_AFTER_LOGIN_REDIRECT_URL } from "../../config";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import authBgImg from "../../images/bg-img.png";
import { InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff, ArrowBack } from "@mui/icons-material";
import useApp from "../../store/contexts/AppContext";
import Copyright from "../../components/Copyright/Copyright";
import { ROUTES } from "../../routes/User/paths";
import { Link as RouterLink } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";

export default function SignUp() {
  const {
    appState: { appName, dataLoading, accessToken },
    registerUserApiCall,
  } = useApp();

  const history = useHistory();

  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({ email: "", username: "", password: "" });

  const hour = new Date().getHours();
  const isDay = hour >= 6 && hour < 18;
  const authTheme = createTheme({
    palette: {
      mode: isDay ? "light" : "dark",
      primary: { main: "#d44401" },
      background: {
        default: isDay ? "#f8f9fa" : "#0a0a0a",
        paper: isDay ? "#ffffff" : "#0a0a0a",
      },
    },
  });

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = (form.get("email") || "").trim();
    const username = (form.get("username") || "").trim();
    const password = form.get("password") || "";

    const errors = { email: "", username: "", password: "" };
    if (!email) errors.email = "Email is required";
    if (!username) errors.username = "Username is required";
    if (!password) errors.password = "Password is required";

    if (errors.email || errors.username || errors.password) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({ email: "", username: "", password: "" });
    registerUserApiCall({ email, username, password });
  };

  useEffect(() => {
    if (accessToken) {
      history.push(REACT_APP_AFTER_LOGIN_REDIRECT_URL);
    }
  }, [accessToken, history]);

  // Prevent rendering if already authenticated
  if (accessToken) {
    return null;
  }

  return (
    <ThemeProvider theme={authTheme}>
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline />
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: `url(${authBgImg})`,
          backgroundRepeat: "no-repeat",
          backgroundColor: isDay ? "#f8f9fa" : "#0a0a0a",
          backgroundSize: "cover",
          // backgroundPosition: "center",
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box sx={{ alignSelf: "flex-start", mb: 2 }}>
            <Button
              component={RouterLink}
              to={ROUTES.landing}
              startIcon={<ArrowBack />}
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              Back to Home
            </Button>
          </Box>
          <Avatar sx={{ m: 1, bgcolor: "primary.main" }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Sign up on {appName}
          </Typography>
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ mt: 1 }}
          >
            <TextField
              margin="dense"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              error={Boolean(formErrors.email)}
              helperText={formErrors.email}
              onChange={() => formErrors.email && setFormErrors(prev => ({ ...prev, email: "" }))}
            />

            <TextField
              margin="dense"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              error={Boolean(formErrors.username)}
              helperText={formErrors.username}
              onChange={() => formErrors.username && setFormErrors(prev => ({ ...prev, username: "" }))}
            />

            <TextField
              margin="dense"
              required
              fullWidth
              name="password"
              label="Password"
              id="password"
              type={showPassword ? "text" : "password"}
              error={Boolean(formErrors.password)}
              helperText={formErrors.password}
              onChange={() => formErrors.password && setFormErrors(prev => ({ ...prev, password: "" }))}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Sign Up{" "}
              {dataLoading && (
                <CircularProgress sx={{ color: "white", ml: 2 }} size={24} />
              )}
            </Button>
            <Grid container>
              <Grid item>
                <Link component={RouterLink} to={ROUTES.login} variant="body2">
                  {"Already have an account? Login"}
                </Link>
              </Grid>
            </Grid>
            <Copyright sx={{ mt: 5 }} />
          </Box>
        </Box>
      </Grid>
    </Grid>
    </ThemeProvider>
  );
}
